import { all, call, fork, put, select, take } from "redux-saga/effects";
import { Pager } from "../../domain/model/common";
import { Contract } from "../../domain/model/contract";
import { Project } from "../../domain/model/project";
import { User } from "../../domain/model/user";
import {
  ICompanyRepository,
  TCompanyIDWithProjectID,
  TCompanyWithProjectID,
} from "../../domain/repository/company_repository";
import {
  IContractRepository,
  TContractIdWithoutCompany,
} from "../../domain/repository/contract_repository";
import { IProjectRepository } from "../../domain/repository/project_repository";
import { createApiClient, IApiClient } from "../../infra/api/client";
import { createCompanyAPI } from "../../infra/api/service/company_api";
import { createContractAPI } from "../../infra/api/service/contract_api";
import { createProjectAPI } from "../../infra/api/service/project_api";
import {
  createCustomerEntriesActionCreator,
  CustomerEntriesActionType,
  ICustomerEntriesActionCreator,
  IRequestGetEntryProjectsAction,
} from "../action/customer_entries_action";
import { GetEntryProjectsByCustomerResponse } from "../response/entry_response";
import { AppState } from "../store/app_state";
import { AuthState } from "../store/auth_state";
import { handleErrorForHandler } from "./handleErrorForHandler";

const apiClient: IApiClient = createApiClient();
const projectRepository: IProjectRepository = createProjectAPI(apiClient);
const companyRepository: ICompanyRepository = createCompanyAPI(apiClient);
const actionCreator: ICustomerEntriesActionCreator =
  createCustomerEntriesActionCreator();
const contractRepository: IContractRepository = createContractAPI(apiClient);

function* handleGetEntryProjectsInCustomerEntries() {
  while (true) {
    try {
      const action: IRequestGetEntryProjectsAction = yield take(
        CustomerEntriesActionType.REQUEST_GET_ENTRY_PROJECTS
      );
      const projectList: Project[] = yield call(
        getAllByEntry,
        action.item.pager
      );
      const companyIDWithProjectIDList: TCompanyIDWithProjectID[] = yield call(
        createCompanyIdWithProjectIdList,
        projectList
      );
      let projectCount: number;
      let companyWithProjectIDList: TCompanyWithProjectID[];
      [projectCount, companyWithProjectIDList] = yield all([
        call(getEntryProjectCount),
        call(getMaskCompanyList, companyIDWithProjectIDList),
      ]);

      const authSelector = (state: AppState) => state.authReducer;
      const _authState: AuthState = yield select(authSelector);
      const user: User | null = _authState.user;

      const contractIds: TContractIdWithoutCompany[] = projectList.map(
        (item) => {
          return {
            projectID: item.id,
            customerID: user!.customer!.id,
          };
        }
      );
      const contractList: Contract[] = yield call(
        getMultiContract,
        contractIds
      );
      const contractIndex: { [key: string]: Contract | null } = {};
      projectList.forEach((item) => {
        for (const contract of contractList) {
          if (contract.projectID === item.id) {
            contractIndex[item.id] = contract;
            break;
          }
        }
      });

      const res: GetEntryProjectsByCustomerResponse =
        new GetEntryProjectsByCustomerResponse();
      res.projects = projectList;
      res.projectCount = projectCount;
      res.companiesWithProjectID = companyWithProjectIDList;
      res.contractIndex = contractIndex;
      yield put(actionCreator.callbackGetEntryProjectsAction(true, res));
    } catch (error) {
      yield fork(handleErrorForHandler, error);
      yield put(actionCreator.callbackGetEntryProjectsAction(false));
    }
  }
}

const createCompanyIdWithProjectIdList = (
  projectList: Project[]
): TCompanyIDWithProjectID[] => {
  return projectList.map((item: Project) => {
    const companyIDWithProjectID: TCompanyIDWithProjectID = {
      companyID: item.companyID,
      projectID: item.id,
    };
    return companyIDWithProjectID;
  });
};

const getAllByEntry = (pager: Pager): Promise<Project[]> => {
  return projectRepository.getAllByEntry(pager.page, pager.offset);
};

const getMaskCompanyList = (
  ids: TCompanyIDWithProjectID[]
): Promise<TCompanyWithProjectID[]> => {
  return companyRepository.getAllByIDsWithMask(ids);
};

const getEntryProjectCount = (): Promise<number | Long> => {
  return projectRepository.getCountByEntry();
};

const getMultiContract = (
  ids: TContractIdWithoutCompany[]
): Promise<Contract[]> => {
  return contractRepository.getMulti(ids);
};

export { handleGetEntryProjectsInCustomerEntries };



// WEBPACK FOOTER //
// ./src/presentation/handler/customer_entries_handler.ts